import React, { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { toast } from "sonner";
import { Briefcase, Users, Rocket, Heart, Mail, User, Phone, FileText } from "lucide-react";
import { WEB3FORMS_ACCESS_KEY } from "../config";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={staggerContainer}
      className={className}
    >
      {children}
    </motion.section>
  );
};

const benefits = [
  { icon: Users, title: "Collaborative Culture", description: "Work with passionate educators and leaders shaping the future of education" },
  { icon: Rocket, title: "Growth Opportunities", description: "Continuous learning and professional development in the education sector" },
  { icon: Heart, title: "Meaningful Impact", description: "Contribute to transforming education for millions of students across India" },
  { icon: Briefcase, title: "Dynamic Environment", description: "Be part of exciting events, conferences, and leadership forums" }
];

const CareerForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          access_key: WEB3FORMS_ACCESS_KEY,
          subject: "New Career Application - GSLC",
          from_name: "GSLC Careers",
          ...formData
        }),
      });
      
      const result = await response.json();
      if (result.success) {
        toast.success("Application submitted successfully! We'll be in touch soon.");
        setFormData({ name: '', email: '', phone: '', position: '', message: '' });
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="career-form">
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Full Name *</label>
          <div className="relative">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              data-testid="career-name-input"
              className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-slate-200 focus:border-red-500 focus:outline-none transition-colors"
              placeholder="Your Name"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Email Address *</label>
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              data-testid="career-email-input"
              className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-slate-200 focus:border-red-500 focus:outline-none transition-colors"
              placeholder="you@example.com"
            />
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Phone Number *</label>
          <div className="relative">
            <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="tel"
              name="phone"
              required
              value={formData.phone}
              onChange={handleChange}
              data-testid="career-phone-input"
              className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-slate-200 focus:border-red-500 focus:outline-none transition-colors"
              placeholder="+91 98765 43210"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Position Interested In *</label>
          <div className="relative">
            <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              name="position"
              required
              value={formData.position}
              onChange={handleChange}
              data-testid="career-position-input"
              className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-slate-200 focus:border-red-500 focus:outline-none transition-colors"
              placeholder="e.g., Event Coordinator, Marketing"
            />
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Tell us about yourself *</label>
        <div className="relative">
          <FileText className="absolute left-4 top-4 w-5 h-5 text-slate-400" />
          <textarea
            name="message"
            required
            rows={5}
            value={formData.message}
            onChange={handleChange}
            data-testid="career-message-input"
            className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-slate-200 focus:border-red-500 focus:outline-none transition-colors resize-none"
            placeholder="Share your experience, skills, and why you'd like to join GSLC..."
          />
        </div>
      </div>

      <p className="text-sm text-slate-500 italic">
        Note: Please email your resume directly to <a href="mailto:pankaj@gslc.co.in" className="text-red-600 hover:underline">pankaj@gslc.co.in</a> after submitting this form.
      </p>

      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        type="submit"
        disabled={isSubmitting}
        data-testid="career-submit-btn"
        className="w-full btn-gradient py-4 text-lg disabled:opacity-50"
      >
        {isSubmitting ? "Submitting..." : "Submit Application"}
      </motion.button>
    </form>
  );
};

const CareersPage = () => {
  return (
    <div className="pt-24">
      {/* Hero */}
      <AnimatedSection className="section-padding bg-gradient-to-br from-yellow-50 via-white to-blue-50">
        <div className="container-custom">
          <motion.div variants={fadeInUp} className="max-w-4xl mx-auto text-center">
            <span className="font-accent text-red-600 text-sm tracking-[0.2em] uppercase">Join Our Team</span>
            <h1 className="font-heading text-4xl md:text-6xl font-semibold mt-4 mb-6 text-slate-800">
              Careers at <span className="gradient-text">GSLC</span>
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
              Be part of a movement that is transforming school leadership and education across India. 
              Join our team of passionate professionals making a real difference.
            </p>
          </motion.div>
        </div>
      </AnimatedSection>

      {/* Why Join GSLC */}
      <AnimatedSection className="section-padding">
        <div className="container-custom">
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <span className="font-accent text-red-600 text-sm tracking-[0.2em] uppercase">Why GSLC</span>
            <h2 className="font-heading text-3xl md:text-5xl font-medium mt-4 text-slate-800">
              Why Work With <span className="gradient-text">Us</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="card-light rounded-2xl p-6 text-center hover-lift"
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-yellow-100 to-red-100 flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-7 h-7 text-red-600" />
                </div>
                <h3 className="font-heading text-lg text-slate-800 mb-2">{benefit.title}</h3>
                <p className="text-slate-500 text-sm">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* Application Form */}
      <AnimatedSection className="section-padding bg-gradient-to-br from-blue-50 via-white to-yellow-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <motion.div variants={fadeInUp} className="text-center mb-10">
              <span className="font-accent text-red-600 text-sm tracking-[0.2em] uppercase">Apply Now</span>
              <h2 className="font-heading text-3xl md:text-5xl font-medium mt-4 text-slate-800">
                Express Your <span className="gradient-text">Interest</span>
              </h2>
              <p className="text-slate-500 mt-4">
                We are always looking for talented individuals. Fill out the form below and we will get back to you.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="card-light rounded-3xl p-8 md:p-10">
              <CareerForm />
            </motion.div>
          </div>
        </div>
      </AnimatedSection>
    </div>
  );
};

export default CareersPage;
